import { forwardRef, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, CalendarDays, ListChecks, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import useAuthUser from "@/hooks/useAuthUser";
import { useMyTasks } from "@/hooks/useMyTasks";
import meetingsService from "@/services/meetings.service";
import { TMeeting } from "@/models/meetings.model";
import { TLeadTask, TPriority } from "@/models/tasks.model";
import { PATH_CALENDAR, PATH_TASKS } from "@/routes/paths";

const PRIORITY_DOT: Record<TPriority, string> = {
  high: "bg-accent-red",
  medium: "bg-accent-amber",
  low: "bg-slate-500",
};

const PANEL_CLASS =
  "overflow-hidden rounded-2xl border-white/[0.08] bg-gradient-to-b from-ink-800/95 to-ink-900/95 p-0 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl";

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function todayBounds() {
  // Local YYYY-MM-DD — the backend reads from/to in the X-Timezone header.
  const now = new Date();
  const day = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return { from: day, to: day };
}

function isOverdue(t: TLeadTask): boolean {
  if (t.isComplete || !t.taskDueDate) return false;
  return new Date(t.taskDueDate).getTime() < Date.now();
}

function isDueToday(taskDueDate?: string): boolean {
  if (!taskDueDate) return false;
  const due = new Date(taskDueDate);
  const now = new Date();
  return (
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate()
  );
}



// ─── Shared peek trigger button ───────────────────────────────────────────────

const PeekTriggerButton = forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    open: boolean;
    label: string;
    count: number;
    icon: React.ElementType;
  }
>(({ open, label, count, icon: Icon, className, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={label}
    {...props}
    className={cn(
      "relative grid h-9 w-9 place-items-center rounded-xl border transition",
      open
        ? "border-brand-400/40 bg-brand-500/10 text-brand-200"
        : "border-white/[0.06] bg-white/[0.02] text-slate-400 hover:border-white/[0.14] hover:bg-white/[0.05] hover:text-white",
      className
    )}
  >
    <Icon className="h-4 w-4" />
    {count > 0 && (
      <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-500 px-0.5 text-[9px] font-bold leading-none text-white ring-2 ring-[rgba(8,10,22,0.55)]">
        {count > 99 ? "99+" : count}
      </span>
    )}
  </button>
));
PeekTriggerButton.displayName = "PeekTriggerButton";

// ─── CalendarPeek ─────────────────────────────────────────────────────────────

export function CalendarPeek() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthUser();
  const [open, setOpen] = useState(false);
  const [meetings, setMeetings] = useState<TMeeting[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const { from, to } = todayBounds();
    setLoading(true);
    meetingsService
      // Only the logged-in user's meetings — same scoping as the calendar's
      // default "My calendar" view.
      .getAll({ from, to, limit: 50, agentId: user.id })
      .then((res) => {
        const active = res.data
          .filter((m) => m.status !== "cancelled" && m.status !== "no_show")
          .sort(
            (a, b) =>
              new Date(a.scheduledAt).getTime() -
              new Date(b.scheduledAt).getTime()
          );
        setMeetings(active);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user.id]);

  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      }),
    []
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <PeekTriggerButton
          open={open}
          label="Calendar peek"
          count={meetings.length}
          icon={CalendarDays}
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className={cn(PANEL_CLASS, "w-[340px]")}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-brand-500/20 blur-3xl" />

        {/* Header */}
        <div className="relative flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="grid h-6 w-6 place-items-center rounded-md bg-brand-500/15 ring-1 ring-brand-400/25">
              <CalendarDays className="h-3 w-3 text-brand-300" />
            </div>
            <div>
              <span className="block text-[12.5px] font-semibold text-white">
                Today
              </span>
              <span className="block text-[10px] text-slate-500">
                {todayLabel}
              </span>
            </div>
          </div>
          {meetings.length > 0 && (
            <span className="rounded-md bg-brand-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-200 ring-1 ring-brand-400/30">
              {meetings.length} meeting{meetings.length !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="relative max-h-[360px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-brand-400" />
            </div>
          ) : meetings.length === 0 ? (
            <div className="grid place-items-center py-8 text-center">
              <CalendarDays className="mx-auto h-5 w-5 text-slate-600" />
              <p className="mt-2 text-[12.5px] text-slate-500">
                No meetings today.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.04] p-1.5">
              {meetings.map((m) => {
                const endTime = new Date(
                  new Date(m.scheduledAt).getTime() + m.duration * 60_000
                );
                const hostName = m.agent
                  ? `${m.agent.firstName ?? ""} ${m.agent.lastName ?? ""}`.trim()
                  : "—";
                return (
                  <div
                    key={m.id}
                    className="flex items-start gap-3 rounded-xl px-2.5 py-2.5 transition hover:bg-white/[0.04]"
                  >
                    {/* Time column */}
                    <div className="w-14 shrink-0 text-right">
                      <span className="block text-[11px] font-medium text-white tabular-nums">
                        {formatTime(m.scheduledAt)}
                      </span>
                      <span className="block text-[10px] text-slate-500 tabular-nums">
                        {formatTime(endTime.toISOString())}
                      </span>
                    </div>
                    {/* Accent line */}
                    <div className="mt-1 w-px self-stretch bg-brand-500/30" />
                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <span className="block truncate text-[12.5px] font-semibold text-white">
                        {m.guestName || "Unnamed"}
                      </span>
                      <span className="block text-[10.5px] text-slate-500">
                        {m.duration} min · {hostName}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="relative border-t border-white/[0.06] bg-white/[0.015]">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate(PATH_CALENDAR);
            }}
            className="flex w-full items-center justify-between px-4 py-2.5 text-[11.5px] text-slate-400 transition hover:text-white"
          >
            <span>Open calendar</span>
            <span>→</span>
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── TasksPeek ────────────────────────────────────────────────────────────────

export function TasksPeek() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const { tasks, isLoading } = useMyTasks(
    { page: 1, pageSize: 50 },
    { scope: "me" }
  );

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const { overdue, dueToday, overdueCount, todayCount } = useMemo(() => {
    const incomplete = tasks.filter((t) => !t.isComplete);
    const overdue = incomplete
      .filter(isOverdue)
      .sort(
        (a, b) =>
          new Date(a.taskDueDate!).getTime() - new Date(b.taskDueDate!).getTime()
      );
    const dueToday = incomplete
      .filter((t) => isDueToday(t.taskDueDate) && !isOverdue(t))
      .sort((a, b) => {
        if (!a.taskDueDate) return 1;
        if (!b.taskDueDate) return -1;
        return (
          new Date(a.taskDueDate).getTime() - new Date(b.taskDueDate).getTime()
        );
      });
    return {
      overdue,
      dueToday,
      overdueCount: overdue.length,
      todayCount: dueToday.length,
    };
  }, [tasks]);

  const displayTasks = [...overdue, ...dueToday];
  const badgeCount = overdueCount + todayCount;

  function taskDueLabel(t: TLeadTask): string {
    if (!t.taskDueDate) return "";
    if (isOverdue(t)) {
      const now = new Date();
      const due = new Date(t.taskDueDate);
      const startOfDay = (d: Date) =>
        new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const dayDiff = Math.abs(
        Math.round((startOfDay(due) - startOfDay(now)) / 86_400_000)
      );
      return dayDiff === 0 ? "Due today" : `${dayDiff}d overdue`;
    }
    return "Due today";
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <PeekTriggerButton
          open={open}
          label="Tasks peek"
          count={badgeCount}
          icon={ListChecks}
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className={cn(PANEL_CLASS, "w-[340px]")}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-brand-500/20 blur-3xl" />

        {/* Header */}
        <div className="relative flex items-center justify-between border-b border-white/[0.06] px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="grid h-6 w-6 place-items-center rounded-md bg-accent-amber/15 ring-1 ring-accent-amber/25">
              <ListChecks className="h-3 w-3 text-accent-amber" />
            </div>
            <div>
              <span className="block text-[12.5px] font-semibold text-white">
                My tasks
              </span>
              <span className="block text-[10px] text-slate-500">
                {overdueCount} overdue · {todayCount} due today
              </span>
            </div>
          </div>
          {badgeCount > 0 && (
            <span className="rounded-md bg-accent-red/15 px-1.5 py-0.5 text-[10px] font-semibold text-accent-red ring-1 ring-accent-red/30">
              {badgeCount} urgent
            </span>
          )}
        </div>

        {/* Body */}
        <div className="relative max-h-[360px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-brand-400" />
            </div>
          ) : displayTasks.length === 0 ? (
            <div className="grid place-items-center py-8 text-center">
              <ListChecks className="mx-auto h-5 w-5 text-slate-600" />
              <p className="mt-2 text-[12.5px] text-slate-500">
                Nothing due — you're all caught up.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-white/[0.04] p-1.5">
              {displayTasks.map((t) => {
                const overdue = isOverdue(t);
                const label = taskDueLabel(t);
                return (
                  <div
                    key={t.id}
                    className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 transition hover:bg-white/[0.04]"
                  >
                    {/* Priority dot */}
                    <span
                      className={cn(
                        "h-1.5 w-1.5 shrink-0 rounded-full",
                        PRIORITY_DOT[t.priority]
                      )}
                    />
                    {/* Title */}
                    <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-slate-100">
                      {t.task}
                    </span>
                    {/* Lead */}
                    {t.leadId && (
                      <span className="shrink-0 text-[11px] text-slate-600">
                        #{t.leadId}
                      </span>
                    )}
                    {/* Due label */}
                    {label && (
                      <span
                        className={cn(
                          "flex shrink-0 items-center gap-1 text-[11px] tabular-nums",
                          overdue
                            ? "font-medium text-accent-red"
                            : "text-slate-500"
                        )}
                      >
                        {overdue && (
                          <AlertCircle className="h-3 w-3 shrink-0" />
                        )}
                        {label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="relative border-t border-white/[0.06] bg-white/[0.015]">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate(PATH_TASKS);
            }}
            className="flex w-full items-center justify-between px-4 py-2.5 text-[11.5px] text-slate-400 transition hover:text-white"
          >
            <span>Open tasks</span>
            <span>→</span>
          </button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
